import moment from 'moment'
import { isCreditNoteDocument } from 'src/utils/creditNoteDocumentHelpers'

const isObjectId = value => /^[a-f\d]{24}$/i.test(String(value || '').trim())

/**
 * Resolve supplier fields after the user confirms:
 * - existing venue supplier, or
 * - create manual supplier from printed name, or
 * - leave blank for the user to pick.
 */
export function resolveSupplierChoiceForProposal(
  proposal = {},
  { suppliers = [], choice = null } = {},
) {
  const printedName = String(
    proposal?.supplierMatch?.printedName ||
      proposal?.createSupplierTitle ||
      proposal?.supplierName ||
      '',
  ).trim()

  if (!choice || choice.type === 'none') {
    return {
      supplierId: '',
      supplierSearchTerm: printedName,
      selectedSupplier: null,
      isManualSupplierSelected: false,
      supplierSageContactNumber: '',
      orderSupplier: '',
    }
  }

  if (choice.type === 'existing') {
    const supplierId = String(
      choice.supplierId || proposal?.supplierMatch?.suggested?.supplierId || '',
    ).trim()
    const venueSupplier = (suppliers || []).find(
      row => String(row?.supplier?._id || '') === supplierId,
    )
    if (venueSupplier && isObjectId(supplierId)) {
      return {
        supplierId,
        supplierSearchTerm: venueSupplier.supplierTitle || '',
        selectedSupplier: venueSupplier,
        isManualSupplierSelected: Boolean(
          venueSupplier?.isManualCreated || venueSupplier?.nonOMSSupplier,
        ),
        supplierSageContactNumber: String(
          venueSupplier?.sageSupplierAccountNumber || '',
        ).trim(),
        orderSupplier: supplierId,
      }
    }
  }

  if (choice.type === 'create') {
    const createTitle = String(choice.title || printedName).trim()
    if (!createTitle) {
      return {
        supplierId: '',
        supplierSearchTerm: '',
        selectedSupplier: null,
        isManualSupplierSelected: false,
        supplierSageContactNumber: '',
        orderSupplier: '',
      }
    }
    return {
      supplierId: createTitle,
      supplierSearchTerm: createTitle,
      selectedSupplier: {
        supplierTitle: createTitle,
        supplier: { title: createTitle, deliveryDays: [] },
        manualOrdersOnly: true,
        isManualCreated: true,
      },
      isManualSupplierSelected: true,
      supplierSageContactNumber: '',
      orderSupplier: createTitle,
    }
  }

  return {
    supplierId: '',
    supplierSearchTerm: printedName,
    selectedSupplier: null,
    isManualSupplierSelected: false,
    supplierSageContactNumber: '',
    orderSupplier: '',
  }
}

/**
 * Map an AI / invoice manual-order proposal into AddInvoiceModal state pieces.
 * Supplier is only applied when `supplierChoice` is provided (after user confirm).
 */
export function mapProposalToManualOrderDraft(
  proposal = {},
  { suppliers = [], supplierChoice = null } = {},
) {
  const supplierFields = resolveSupplierChoiceForProposal(proposal, {
    suppliers,
    choice: supplierChoice,
  })

  const orderedDate = proposal.orderDate
    ? moment(proposal.orderDate, 'YYYY-MM-DD')
    : null
  const deliveryDate = proposal.deliveryDate
    ? moment(proposal.deliveryDate, 'YYYY-MM-DD')
    : orderedDate
    ? orderedDate.clone()
    : null

  // Products are filled in a second step after supplier confirm.
  const products = []

  return {
    ...supplierFields,
    orderedDate: orderedDate && orderedDate.isValid() ? orderedDate : null,
    deliveryDate: deliveryDate && deliveryDate.isValid() ? deliveryDate : null,
    poNumber: String(proposal.poNumber || '').trim(),
    selectedProducts: products,
    orderPatch: {
      supplier: supplierFields.orderSupplier,
      supplierSageContactNumber: supplierFields.supplierSageContactNumber,
      PONumber: String(proposal.poNumber || '').trim(),
      orderedDate:
        orderedDate && orderedDate.isValid() ? orderedDate : undefined,
      deliveryDate:
        deliveryDate && deliveryDate.isValid() ? deliveryDate : undefined,
      products,
    },
  }
}

/**
 * Map catalogue-resolved (or plain guessed) product lines into table rows.
 */
export function mapResolvedProductsToOrderRows(
  products = [],
  { orderedDate = null, deliveryDate = null } = {},
) {
  return (Array.isArray(products) ? products : []).map((line, index) => {
    const unitPrice = Number(
      line?.OMSPrice != null ? line.OMSPrice : line?.unitPrice,
    )
    const hasVat = Boolean(line?.hasVat)
    const priceIncludesVat = hasVat && Boolean(line?.priceIncludesVat)
    return {
      key: `ai-prod-${Date.now()}-${index}`,
      product: isObjectId(line?.product) ? String(line.product) : '',
      title: String(line?.title || '').trim(),
      sku: String(line?.sku || '').trim(),
      size: String(line?.size || '').trim(),
      type: String(line?.type || '').trim(),
      UoM: String(line?.UoM || '').trim(),
      quantity:
        Number.isFinite(Number(line?.quantity)) && Number(line.quantity) > 0
          ? Number(line.quantity)
          : 1,
      OMSPrice: Number.isFinite(unitPrice) && unitPrice >= 0 ? unitPrice : 0,
      price: Number.isFinite(unitPrice) && unitPrice >= 0 ? unitPrice : 0,
      hasVat,
      priceIncludesVat,
      nominalCode: String(line?.nominalCode || '').trim(),
      orderedDate,
      deliveryDate,
    }
  })
}

/**
 * Whether the Invoices Actions column should offer Create order from invoice.
 * Credit notes must not create a new PO. Keep the old Orders deep-link path too.
 */
export function canCreateMatchingManualOrder(invoice = {}) {
  if (!invoice?.id && !invoice?._id) return false
  if (isCreditNoteDocument(invoice)) return false

  const status = String(invoice.status || '').trim()
  if (
    status === 'attachedToPO' ||
    status === 'reconciled' ||
    status === 'sentToXero' ||
    status === 'archived'
  ) {
    return false
  }

  if (invoice.linkedOrderId) return false
  if (
    Array.isArray(invoice.matchedOrderIds) &&
    invoice.matchedOrderIds.length > 0 &&
    (invoice.matchingStatus === 'match_found' ||
      invoice.matchingStatus === 'matched')
  ) {
    return false
  }

  const matchingStatus = String(invoice.matchingStatus || 'unprocessed')
  if (matchingStatus === 'no_match') return true
  if (matchingStatus === 'needs_action') {
    const reason = String(invoice.matchingFailureReason || '').trim()
    return (
      reason === 'po_not_found' ||
      reason === 'missing_po' ||
      reason === 'no_match' ||
      !String(invoice.purchaseOrderNumber || '').trim()
    )
  }
  if (matchingStatus === 'unprocessed') {
    return !String(invoice.purchaseOrderNumber || '').trim()
  }

  return false
}

/**
 * Classic manual orders lock Ordered / Delivery once lines exist.
 * AI / invoice proposals must keep those dates editable so the chef can
 * correct what Ollie extracted. Supplier lock is separate.
 */
export function shouldLockManualOrderDates({
  aiProposalApplied = false,
  selectedProductCount = 0,
} = {}) {
  if (aiProposalApplied) return false
  return Number(selectedProductCount) > 0
}
